import React from 'react'
import { DocumentPage, Text, View, StyleSheet, useStyle } from '~/lib/pdf'
import Moment from 'moment'

const isNotBlank = (item) => ![null, undefined, ''].includes(item)

export const defaultStyle = {
  xMargin: 0.4,
  yMargin: 0.4,
  size: 'Letter',
  lineHeightMultiplier: 1.0,
  listLineHeightMultiplier: 1.0,
  primaryFontFamily: 'Tinos',
  headingFontSize: 24,
  bodyFontSize: 12,
  summaryHeading: 'Summary',
  workHeading: 'Experience',
  educationHeading: 'Education',
  volunteerHeading: 'Leadership & Activities',
  skillsHeading: 'Skills & Interests',
  presentDateField: 'Present',
}

export const getOptionalEditorFields = (locale) => ({
  basicsJobTitle: false,
  basicsLocation: true,
  basicsSummary: locale === 'en_gb' ? true : false,
  basicsWebsite: false,
  basicsLinkedin: false,
  volunteer: true,
  workLocation: true,
  workSummary: false,
  educationCourses: true,
  educationLocation: true,
  educationGpa: true,
  educationTenure: true,
  skillProficiency: false,
  additionalInfo: false,
  awards: false,
})

export const Template = ({ jsonDocument, templateStyle, title, locale }) => {
  const { bodyFontSize, headingFontSize, summaryHeading } = templateStyle
  const styles = StyleSheet.create({
    heading: {
      width: '100%',
      textAlign: 'center',
      fontSize: headingFontSize,
    },
    contactSection: {
      flexDirection: 'row',
      fontSize: bodyFontSize,
      justifyContent: 'center',
      paddingBottom: 4,
    },
    contactItem: {},
    contactSpacer: {
      padding: '0 8px',
    },
    sectionTitle: {
      fontSize: bodyFontSize,
      fontWeight: 'bold',
      marginTop: '15px',
      borderBottom: '1px solid #000000',
      marginBottom: '3px',
    },
    skillsSection: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
    },
    summarySection: {
      fontSize: bodyFontSize,
    },
  })

  const { basics, education, work, volunteer, skills } = jsonDocument
  const { name, email, phone, location, summary } = basics || {}
  const { address, city, region, postalCode } = location || {}

  const regionPostalCode = [region, postalCode].filter(isNotBlank).join(' ')
  const cityStateZip = city ? `${city}, ${regionPostalCode}` : regionPostalCode
  const contactItems = [address, cityStateZip, email, phone].filter(isNotBlank)

  return (
    <DocumentPage templateStyle={templateStyle} title={title}>
      <Text style={styles.heading}>{name}</Text>
      <View style={styles.contactSection}>
        {contactItems.map((item, index) => {
          return (
            <View key={index} style={{ flexDirection: 'row' }}>
              <Text style={styles.contactItem}>{item}</Text>
              {index < contactItems.length - 1 && (
                <Text style={styles.contactSpacer}>|</Text>
              )}
            </View>
          )
        })}
      </View>
      <View>
        {locale === 'en_gb' && summary && (
          <>
            <Text style={styles.sectionTitle}>{summaryHeading}</Text>
            <Text style={styles.summarySection}>{summary}</Text>
          </>
        )}
        {education && education.length > 0 && (
          <EducationSection
            education={education}
            styles={styles}
            locale={locale}
          />
        )}
        {work && work.length > 0 && (
          <WorkSection work={work} styles={styles} locale={locale} />
        )}
        {volunteer && volunteer.length > 0 && (
          <VolunteerSection
            volunteer={volunteer}
            styles={styles}
            locale={locale}
          />
        )}
        {skills && skills.length > 0 && (
          <SkillSection skills={skills} styles={styles} locale={locale} />
        )}
      </View>
    </DocumentPage>
  )
}

const EducationSection = ({ education, styles, locale }) => {
  const {
    templateStyle: { educationHeading },
  } = useStyle()
  return (
    <>
      <Text style={styles.sectionTitle}>{educationHeading}</Text>
      {education.map((edItem, index) => (
        <EducationView
          education={edItem}
          key={`education-${index}`}
          locale={locale}
        />
      ))}
    </>
  )
}

const WorkSection = ({ work, styles, locale }) => {
  const {
    templateStyle: { workHeading },
  } = useStyle()
  return (
    <>
      <Text style={styles.sectionTitle}>{workHeading}</Text>
      {work.map((job, index) => (
        <JobView job={job} key={`job-${index}`} locale={locale} />
      ))}
    </>
  )
}

const VolunteerSection = ({ volunteer, styles, locale }) => {
  const {
    templateStyle: { volunteerHeading },
  } = useStyle()
  return (
    <>
      <Text style={styles.sectionTitle}>{volunteerHeading}</Text>
      {volunteer.map((item, index) => (
        <VolunteerView
          volunteer={item}
          key={`volunteer-${index}`}
          locale={locale}
        />
      ))}
    </>
  )
}

const SkillSection = ({ skills, styles }) => {
  const {
    templateStyle: { skillsHeading },
  } = useStyle()
  return (
    <View>
      <Text style={styles.sectionTitle}>{skillsHeading}</Text>
      <View style={{ width: '100%' }}>
        {skills?.map((skill, index) => (
          <SkillView skill={skill} key={`skill-${index}`} />
        ))}
      </View>
    </View>
  )
}

const SkillView = ({ skill }) => {
  const {
    templateStyle: { bodyFontSize },
  } = useStyle()
  const styles = StyleSheet.create({
    container: {
      fontSize: bodyFontSize,
      paddingBottom: '3px',
      flexDirection: 'row',
      justifyItems: 'start',
    },
    skillName: {
      fontWeight: 'bold',
      paddingRight: '4px',
    },
    skillList: { textOverflow: 'wrap' },
  })
  if (!skill.name) {
    return null
  }
  return (
    <View style={styles.container}>
      <View style={styles.skillName}>
        <Text>{skill.name}:</Text>
      </View>
      <View style={styles.skillList}>
        <Text style={{ maxWidth: '90%' }}>{skill.keywords?.join(', ')}</Text>
      </View>
    </View>
  )
}

const VolunteerView = ({ volunteer, locale }) => {
  const {
    templateStyle: { bodyFontSize },
  } = useStyle()
  const styles = StyleSheet.create({
    container: {
      fontSize: bodyFontSize,
      paddingBottom: '15px',
    },
    row: { flexDirection: 'row', justifyContent: 'space-between' },
    organization: {
      fontWeight: 'bold',
    },
    position: {
      fontWeight: 'bold',
    },
    tenure: {
      fontStyle: 'italic',
    },
  })
  if (!volunteer.position) {
    return null
  }
  return (
    <View style={styles.container} wrap={false}>
      <View style={styles.row}>
        <Text style={styles.organization}>{volunteer.organization}</Text>
        <Text>{volunteer.location}</Text>
      </View>
      <View style={styles.row}>
        <Text style={styles.position}>{volunteer.position}</Text>
        <Text style={styles.tenure}>
          <Tenure item={volunteer} locale={locale} />
        </Text>
      </View>
      {volunteer.highlights &&
        volunteer.highlights.map((highlight, index) => (
          <BulletLine key={`volunteer-highlight-${index}`}>
            {highlight}
          </BulletLine>
        ))}
    </View>
  )
}

const JobView = ({ job, locale }) => {
  const {
    templateStyle: { bodyFontSize },
  } = useStyle()
  const styles = StyleSheet.create({
    jobContainer: {
      fontSize: bodyFontSize,
      paddingBottom: '15px',
    },
    jobRow: { flexDirection: 'row', justifyContent: 'space-between' },
    jobCompany: {
      fontWeight: 'bold',
    },
    jobPosition: {
      fontWeight: 'bold',
    },
    jobTenure: {
      fontStyle: 'italic',
    },
  })
  if (!job.position && !job.name) {
    return null
  }

  return (
    <View style={styles.jobContainer} wrap={false}>
      <View style={styles.jobRow}>
        <Text style={styles.jobCompany}>{job.name}</Text>
        <Text>{job.location}</Text>
      </View>
      <View style={styles.jobRow}>
        <Text style={styles.jobPosition}>{job.position}</Text>
        <Text style={styles.jobTenure}>
          <Tenure item={job} locale={locale} />
        </Text>
      </View>
      {job.highlights &&
        job.highlights.map((highlight, index) => (
          <BulletLine key={`highlight-${index}`}>{highlight}</BulletLine>
        ))}
    </View>
  )
}

const EducationView = ({ education, locale }) => {
  const {
    templateStyle: { lineHeightMultiplier, bodyFontSize },
  } = useStyle()
  const styles = StyleSheet.create({
    container: {
      paddingBottom: '15px',
      fontSize: bodyFontSize,
    },
    institution: {
      fontWeight: 'bold',
      paddingRight: '8px',
    },
    tenure: {
      fontStyle: 'italic',
    },
    highlight: {
      marginTop: '4px',
      lineHeight: `${16 * lineHeightMultiplier}px`,
    },
    educationRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: '3px',
    },
  })

  if (!education.institution && !education.studyType) {
    return null
  }

  const { institution, area, studyType, location, score } = education

  const degreeConcentration = [studyType, area].filter(isNotBlank).join(', ')
  const degreeConcentrationGpa = score
    ? `${degreeConcentration}. GPA: ${score}`
    : degreeConcentration

  return (
    <View style={styles.container} wrap={false}>
      <View style={styles.educationRow}>
        <Text style={styles.institution}>{institution}</Text>
        <Text>{location}</Text>
      </View>
      <View style={styles.educationRow}>
        <Text>{degreeConcentrationGpa}</Text>
        <Text style={styles.tenure}>
          <Tenure item={education} locale={locale} />
        </Text>
      </View>
      {education.courses &&
        education.courses.map((course, index) => (
          <Text key={`highlight-${index}`} style={styles.highlight}>
            {course}
          </Text>
        ))}
    </View>
  )
}
const BulletLine = ({ children }) => {
  const {
    templateStyle: { listLineHeightMultiplier },
  } = useStyle()

  const styles = StyleSheet.create({
    highlightBullet: {
      padding: '0 8px',
    },
    highlightText: {
      lineHeight: `${16 * listLineHeightMultiplier}px`,
    },
    container: { flexDirection: 'row', paddingRight: '20px' },
  })

  const hcbNone = (word) => [word]

  return (
    <View style={styles.container}>
      <Text style={styles.highlightBullet}>&#8226;</Text>
      <Text hyphenationCallback={hcbNone} style={styles.highlightText}>
        {children}
      </Text>
    </View>
  )
}

const momentFormat = (date, nullValue = '', locale) => {
  if (!date || [undefined, null, ''].includes(date)) {
    return nullValue
  }
  try {
    const result = Moment(date).locale(locale).format('MMM YYYY')
    if (result === 'Invalid date') return nullValue
    return result
  } catch {
    return nullValue
  }
}

const Tenure = ({ item, locale }) => {
  const {
    templateStyle: { presentDateField },
  } = useStyle()

  if (!item.startDate && !item.endDate) {
    return null
  }

  const displayLocale = locale || 'en'
  const formattedStart = momentFormat(item.startDate, '', displayLocale)
  const formattedEnd = momentFormat(
    item.endDate,
    presentDateField,
    displayLocale,
  )

  return [formattedStart, formattedEnd].filter(isNotBlank).join(' - ')
}
